import {
  COLUMNS,
  columnIndexOf,
  formatDuration,
  gameDurationMs,
  modalityName,
  pad,
  roundDurationMs,
  type EventState,
  type RoundState,
} from '@bingo/shared';
import { useEffect, useState } from 'react';
import { serverNow } from '../lib/clock.js';
import { Ball, BigBall } from './Ball.js';
import { Board } from './Board.js';
import { MiniCard } from './MiniCard.js';

/**
 * O palco do telão — tradução 1:1 do `<div class="app">` do protótipo.
 *
 * A estrutura do DOM e as classes são as mesmas porque o CSS é o mesmo arquivo,
 * copiado do protótipo. Mudar nomes de classe aqui quebraria o visual.
 */

interface StageProps {
  state: EventState;
  round: RoundState | null;
  bigBall: number | null;
  animation: 'roll' | 'shake' | 'flash' | null;
  /** Número provisório mostrado durante o suspense. */
  suspenseBall: number | null;
  /** Cabeçalho extra do telão (ex.: aviso de áudio mudo). */
  headerExtra?: React.ReactNode;
  /** Modo globo físico no /controle. */
  onPick?: (ball: number) => void;
  /** O /controle mostra os botões; o telão nunca. */
  controls?: React.ReactNode;
  autoBar?: React.ReactNode;
}

/** Relógio que anda sozinho, recalculado a cada segundo. */
function useTicker(): number {
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return serverNow();
}

export function Stage({
  state,
  round,
  bigBall,
  animation,
  suspenseBall,
  headerExtra,
  onPick,
  controls,
  autoBar,
}: StageProps) {
  const now = useTicker();

  const balls = round?.balls ?? [];
  const drawn = balls.map((ball) => ball.number);
  const lastBall = drawn.at(-1) ?? null;
  const shown = suspenseBall ?? bigBall;

  // As três últimas ANTES da atual, como no protótipo: `drawn.slice(-4,-1)`
  // invertido, com buracos quando ainda não há três.
  const previous = drawn.slice(-4, -1).reverse();

  const lastWinner = round?.winners.at(-1) ?? null;

  const roundMs = round
    ? roundDurationMs(
        {
          startedAt: new Date(round.startedAt),
          endedAt: round.endedAt ? new Date(round.endedAt) : null,
          ballsCount: balls.length,
          lastWinner: lastWinner
            ? { confirmedAt: new Date(lastWinner.confirmedAt), ballsAtWin: lastWinner.ballsAtWin }
            : null,
        },
        new Date(now),
      )
    : 0;

  const roundStopped =
    Boolean(round?.endedAt) || Boolean(lastWinner && lastWinner.ballsAtWin === balls.length);

  const gameMs = state.game
    ? gameDurationMs(
        {
          startedAt: new Date(state.game.startedAt),
          endedAt: state.game.endedAt ? new Date(state.game.endedAt) : null,
        },
        new Date(now),
      )
    : 0;

  const winners = round?.winners.length ?? 0;

  const roundLabel = !state.game
    ? 'Aguardando partida'
    : state.game.endedAt
      ? `Partida ${state.game.number} encerrada`
      : `Partida ${state.game.number} · Rodada ${round?.number ?? 1}` +
        (winners > 0 ? ` · ${winners} ganhador${winners > 1 ? 'es' : ''}` : '');

  const modeLabel = {
    MANUAL: 'Sorteio manual',
    AUTO: `Automático · ${state.settings.autoIntervalSec}s`,
    GLOBE: 'Globo físico',
  }[state.settings.drawMode];

  return (
    <div className="app" id="app">
      <header className="top">
        <div className="brand">
          <div className="ev" id="evName">
            {state.event.name || 'Bingo'}
          </div>
          <div className="rd" id="rdLbl">
            {roundLabel}
          </div>
        </div>

        <div className="valendo" id="valendo">
          <MiniCard modality={round?.modality ?? 'FULL_CARD'} />
          <div style={{ textAlign: 'left' }}>
            <div className="lbl">Valendo</div>
            <div className="md" id="mdName">
              {modalityName(round?.modality ?? 'FULL_CARD')}
            </div>
          </div>
        </div>

        <div className="stats">
          {headerExtra}
          <div className="stat">
            <div className="k">Prêmio</div>
            <div className="v prize" id="prizeLbl">
              {round?.prizeText || '—'}
            </div>
          </div>
          <div className="stat">
            <div className="k">Bolas</div>
            <div className="v" id="countLbl">
              {balls.length}/75
            </div>
          </div>
          <div className={`stat clock${roundStopped ? ' stopped' : ''}`} id="clkRound">
            <div className="k">
              <i className="dot" />
              Rodada
            </div>
            <div className="v" id="tRound">
              {formatDuration(roundMs)}
            </div>
          </div>
          <div className={`stat clock${state.game?.endedAt ? ' stopped' : ''}`} id="clkGame">
            <div className="k">
              <i className="dot" />
              Partida
            </div>
            <div className="v" id="tGame">
              {formatDuration(gameMs)}
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <Board drawn={drawn} lastBall={lastBall} {...(onPick ? { onPick } : {})} />

        <section className="side">
          <div className="side-head">
            <span id="sideMode">{modeLabel}</span>
            <span>
              Bola <b id="ballNo">{balls.length}</b>
            </span>
          </div>

          <div className="spot">
            <BigBall ball={shown} animation={animation} />
          </div>

          <div className="lastrow">
            <div className="k">Últimas bolas</div>
            <div className="last3" id="last3">
              {[0, 1, 2].map((index) => (
                <Ball key={index} ball={previous[index] ?? null} />
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gap: '1.2vh' }}>
            {autoBar}
            {controls}
          </div>
        </section>
      </main>
    </div>
  );
}

/** Letra falada de uma bola, para legendas. */
export function ballLetter(ball: number): string {
  return COLUMNS[columnIndexOf(ball)]?.letter ?? '';
}

export { pad };
